import { api } from '@/services/api/AxiosBaseService'
import type { AuthenticatedUser } from '@/stores/authStore'

// `warning` vem quando o usuário não tem e-mail: entra sem OTP, mas com aviso.
type AuthenticatedPayload = AuthenticatedUser & { accessToken: string; warning?: string }

/** Desafio de verificação em duas etapas (OTP) devolvido pelo sign-in. */
export interface OtpChallenge {
  otpRequired: true
  challengeId: string
  channel: string
  /** Destino mascarado para exibição (ex.: "jo****@gmail.com"). */
  destination: string
  expiresInSeconds: number
  resendInSeconds: number
}

/** Resultado do sign-in: ou exige OTP, ou (compat) já autentica direto. */
export type SignInResult =
  | { kind: 'otp'; challenge: OtpChallenge }
  | { kind: 'authenticated'; user: AuthenticatedPayload }

interface SingleResponse<T> { data: T }

export const AuthService = {
  /** Etapa 1: valida credenciais. Normalmente devolve um desafio OTP. */
  async signIn(username: string, password: string): Promise<SignInResult> {
    const res = await api.post<SingleResponse<OtpChallenge | AuthenticatedPayload>>('auth/sign-in', { username, password })
    const data = res.data.data
    if (data && (data as OtpChallenge).otpRequired) {
      return { kind: 'otp', challenge: data as OtpChallenge }
    }
    return { kind: 'authenticated', user: data as AuthenticatedPayload }
  },

  /** Etapa 2: confere o código OTP e conclui o login (usuário + token). */
  async verifyOtp(challengeId: string, code: string): Promise<AuthenticatedPayload> {
    const res = await api.post<SingleResponse<AuthenticatedPayload>>('auth/verify-otp', { challengeId, code })
    return res.data.data
  },

  /** Reenvia um novo código para o mesmo desafio. */
  async resendOtp(challengeId: string): Promise<{ destination: string; expiresInSeconds: number; resendInSeconds: number }> {
    const res = await api.post<SingleResponse<{ destination: string; expiresInSeconds: number; resendInSeconds: number }>>('auth/resend-otp', { challengeId })
    return res.data.data
  },

  async signOut(): Promise<void> {
    await api.post('auth/sign-out')
  },

  /** Usuário atual com permissões atualizadas (refletir mudanças sem novo login). */
  async me(): Promise<AuthenticatedUser> {
    const res = await api.get<SingleResponse<AuthenticatedUser>>('auth/me')
    return res.data.data
  },
}
